import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { publicApi } from "@/lib/api";

const FALLBACK_LOGO = "/assets/logos/bsc-alliance-transparent.png";
const NAV = [
  { to: "/", label: "Home" },
  { to: "/alliance", label: "Alliance" },
  { to: "/manifesto", label: "Manifesto" },
  { to: "/research", label: "Research" },
  { to: "/beyond-science", label: "Beyond Science" },
  { to: "/founder", label: "Founder" },
  { to: "/founding-members", label: "Members" },
  { to: "/programs", label: "Programs" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [logo, setLogo] = useState(FALLBACK_LOGO);
  const loc = useLocation();
  const transparentRoutes = ["/", "/manifesto"];
  const transparent = transparentRoutes.includes(loc.pathname) && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    publicApi
      .siteSettings()
      .then((s) => {
        const url = s?.logo_assets?.primary || s?.logo_assets?.dark_emblem;
        if (url) setLogo(url);
      })
      .catch(() => {});
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        transparent
          ? "bg-transparent border-transparent"
          : "bg-[#050B14]/85 backdrop-blur-xl border-b border-white/10"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 h-16 md:h-20 lg:h-[88px] flex items-center justify-between gap-4 text-white">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 lg:gap-4 group min-w-0 shrink-0"
        >
          <img
            src={logo}
            alt="Beyond Science & Consciousness Alliance"
            data-testid="header-logo-img"
            className="h-11 md:h-12 lg:h-14 w-auto object-contain drop-shadow-[0_0_18px_rgba(212,175,55,0.18)]"
          />
          <div
            data-testid="header-brand-text"
            className="hidden sm:flex flex-col leading-tight min-w-0"
          >
            <span className="text-white font-serif tracking-wide text-[15px] lg:text-[15px] xl:text-[17px] whitespace-nowrap">
              <span className="hidden xl:inline">Science + Consciousness Alliance</span>
              <span className="xl:hidden">BSC Alliance</span>
            </span>
            <span className="mt-0.5 text-[10px] lg:text-[10.5px] tracking-[0.22em] text-white/55 uppercase whitespace-nowrap">
              BSCA · BSCALLIANCE.ORG
            </span>
          </div>
          <span className="sm:hidden text-white font-serif tracking-[0.18em] text-sm">BSCA</span>
          <span className="sr-only">Beyond Science &amp; Consciousness Alliance — bscalliance.org</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7" data-testid="nav-primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] xl:text-sm font-medium tracking-wide bsca-link whitespace-nowrap ${
                  isActive ? "text-[#D4AF37]" : "text-white/85 hover:text-white"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/engage#contact"
            data-testid="nav-link-contact"
            className="text-sm text-white/80 hover:text-white bsca-link"
          >
            Contact
          </Link>
          <Link
            to="/engage#join"
            data-testid="btn-join-header"
            className="bsca-lift inline-flex items-center bg-[#D4AF37] text-[#050B14] text-sm font-medium px-4 py-2 rounded-sm hover:bg-[#F0D060] whitespace-nowrap"
          >
            Join BSCA
          </Link>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="btn-mobile-menu"
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center text-white"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-[#050B14] text-white border-l border-white/10 w-[85%]">
            <SheetTitle className="sr-only">BSCA Navigation</SheetTitle>
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <img src={logo} alt="BSC Alliance" className="h-10 w-auto object-contain" />
                <div className="flex flex-col leading-tight">
                  <span className="text-white font-serif text-sm">BSC Alliance</span>
                  <span className="text-[9.5px] tracking-[0.22em] text-white/55 uppercase">
                    BSCALLIANCE.ORG
                  </span>
                </div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" className="text-white/70">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-col gap-1" data-testid="nav-mobile">
              {NAV.concat([{ to: "/engage#contact", label: "Contact" }]).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 border-b border-white/10 font-serif text-xl ${
                      isActive ? "text-[#D4AF37]" : "text-white"
                    }`
                  }
                  end={item.to === "/"}
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/engage#join"
                onClick={() => setOpen(false)}
                data-testid="btn-mobile-join"
                className="mt-6 inline-flex items-center justify-center bg-[#D4AF37] text-[#050B14] font-medium px-5 py-3 rounded-sm"
              >
                Join BSCA
              </Link>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
